import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Shield, Search } from "lucide-react";
import { toast } from "sonner";

const ACTION_COLORS = {
  "auth.login.success": "default",
  "auth.login.failed": "destructive",
  "auth.login.blocked_lockout": "destructive",
  "auth.register": "default",
  "application.status_change": "outline",
  "evaluation.sent": "outline",
  "payment.success.application_fee": "default",
  "payment.success.enrollment_deposit": "default",
  "payment.success.us_history_deposit": "default",
  "payment.success.balance_installment": "default",
  "payment.update_status": "outline",
};

export default function AdminAuditLogs() {
  const { lang } = useLang();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/admin/audit-logs?limit=300")
      .then(r => setLogs(r.data))
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = logs.filter(l => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return ((l.action || "") + " " + (l.actor_email || "") + " " + (l.target || "")).toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-audit-logs">
      <p className="overline mb-1">OFA France · Admin · Sécurité</p>
      <div className="flex items-center gap-3 mb-6">
        <Shield className="w-8 h-8 text-accent" />
        <h1 className="font-display font-bold text-3xl tracking-tighter">
          {lang === "fr" ? "Sécurité · Audit" : "Security · Audit"}
        </h1>
      </div>

      {/* Legal PDFs */}
      <Card className="mb-6 border-accent/30">
        <CardContent className="p-5">
          <h2 className="font-display font-semibold text-lg mb-3">
            {lang === "fr" ? "Documents légaux (brouillons)" : "Legal documents (drafts)"}
          </h2>
          <p className="text-xs text-muted-foreground mb-4">
            {lang === "fr"
              ? "PDF imprimables des CGU et de la Politique de confidentialité, à soumettre à votre conseil juridique avant publication finale."
              : "Printable PDFs of Terms and Privacy Policy, to submit to your legal counsel before final publication."}
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="border border-border rounded-lg p-4">
              <div className="font-display font-semibold text-sm mb-2">
                {lang === "fr" ? "Politique de confidentialité" : "Privacy Policy"}
              </div>
              <div className="flex gap-2">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/legal/privacy?lang=fr`}
                  data-testid="dl-privacy-pdf-fr"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors text-xs font-semibold"
                  target="_blank" rel="noopener noreferrer"
                >📄 FR · PDF</a>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/legal/privacy?lang=en`}
                  data-testid="dl-privacy-pdf-en"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors text-xs font-semibold"
                  target="_blank" rel="noopener noreferrer"
                >📄 EN · PDF</a>
              </div>
            </div>
            <div className="border border-border rounded-lg p-4">
              <div className="font-display font-semibold text-sm mb-2">
                {lang === "fr" ? "Conditions d'utilisation" : "Terms of Use"}
              </div>
              <div className="flex gap-2">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/legal/terms?lang=fr`}
                  data-testid="dl-terms-pdf-fr"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors text-xs font-semibold"
                  target="_blank" rel="noopener noreferrer"
                >📄 FR · PDF</a>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/legal/terms?lang=en`}
                  data-testid="dl-terms-pdf-en"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors text-xs font-semibold"
                  target="_blank" rel="noopener noreferrer"
                >📄 EN · PDF</a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <h2 className="font-display font-semibold text-xl mb-2 mt-8">
        {lang === "fr" ? "Journal d'audit" : "Audit log"}
      </h2>
      <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
        {lang === "fr"
          ? "Toutes les actions sensibles (connexions, changements de statut, paiements, évaluations) sont tracées avec horodatage UTC, acteur et IP. Conservation : 12 mois maximum."
          : "All sensitive actions (logins, status changes, payments, evaluations) are tracked with UTC timestamp, actor and IP. Retention: 12 months max."}
      </p>
      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="audit-search"
          placeholder={lang === "fr" ? "Filtrer (action, email, cible)…" : "Filter (action, email, target)…"}
          className="pl-9"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
      </div>
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">{lang === "fr" ? "Chargement…" : "Loading…"}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead className="bg-secondary/50 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-3">{lang === "fr" ? "Horodatage (UTC)" : "Timestamp (UTC)"}</th>
                    <th className="text-left p-3">{lang === "fr" ? "Action" : "Action"}</th>
                    <th className="text-left p-3">{lang === "fr" ? "Acteur" : "Actor"}</th>
                    <th className="text-left p-3">{lang === "fr" ? "Cible / IP" : "Target / IP"}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan="4" className="p-8 text-center text-muted-foreground">
                      {lang === "fr" ? "Aucune entrée." : "No entries."}
                    </td></tr>
                  ) : filtered.map(l => {
                    let tsDisplay = "—";
                    if (l.ts) {
                      const d = new Date(l.ts);
                      if (!isNaN(d.getTime())) {
                        tsDisplay = d.toISOString().replace("T", " ").slice(0, 19);
                      } else {
                        tsDisplay = String(l.ts).slice(0, 19);
                      }
                    }
                    return (
                    <tr key={l.id} className="border-t border-border hover:bg-secondary/30" data-testid={`audit-row-${l.id}`}>
                      <td className="p-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{tsDisplay}</td>
                      <td className="p-3">
                        <Badge variant={ACTION_COLORS[l.action] || "secondary"} className="text-[10px] font-mono">
                          {l.action}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs">
                        {l.actor_email || <span className="text-muted-foreground italic">anonymous</span>}
                        {l.actor_role && <div className="text-[10px] text-muted-foreground">{l.actor_role}</div>}
                      </td>
                      <td className="p-3 text-xs font-mono text-muted-foreground truncate max-w-xs">
                        {l.target || "—"}
                        {l.details?.ip && <div className="text-[10px]">IP: {l.details.ip}</div>}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
